import { motion } from "framer-motion";
import { ArrowRight, FileText, FlaskConical, ShieldCheck, Sparkles, Stethoscope } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/branding";

const HERO_IMG = "/logo-recetrans.png";

const steps = [
  {
    icon: Stethoscope,
    title: "Elige a tu médico",
    text: "Selecciona al profesional de salud trans que lleva tu seguimiento THAG.",
  },
  {
    icon: FlaskConical,
    title: "Sube tus laboratorios",
    text: "Adjunta tus laboratorios recientes y tu última receta en PDF o imagen.",
  },
  {
    icon: Sparkles,
    title: "Revisión asistida por Profesionales",
    text: "Tu médico revisa un borrador inteligente de tu receta y nota de evolución.",
  },
  {
    icon: FileText,
    title: "Recibe tu receta",
    text: "Descarga tu receta firmada en PDF directamente desde tu panel.",
  },
];

const HERO_TEXT_ANIM = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const HERO_IMG_ANIM = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.6, delay: 0.15 },
};

export default function LandingPage() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TransSalud" className="h-9 w-9 object-contain" />
            <span className="font-heading font-semibold text-lg">
              TransSalud <span className="text-primary">· THAG</span>
            </span>
          </div>
          <Link to="/auth">
            <Button variant="outline" className="rounded-full" data-testid="landing-login-btn">
              Iniciar sesión
            </Button>
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <motion.div {...HERO_TEXT_ANIM}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary bg-accent rounded-full px-4 py-1.5 mb-6">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
            Terapia Hormonal de Afirmación de Género
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Tu receta THAG,
            <br />
            <span className="text-primary">sin filas ni esperas.</span>
          </h1>
          <p className="text-base text-muted-foreground max-w-md mb-8 leading-relaxed">
            Renueva tu receta de terapia hormonal en línea: sube tus laboratorios, comparte cómo te
            has sentido y recibe tu receta y nota de evolución revisada por tu médico.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/auth?registro=1">
              <Button size="lg" className="rounded-full px-8" data-testid="landing-cta-register">
                Solicitar mi receta <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <Link to="/auth?registro=1&rol=professional">
              <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="landing-cta-professional">
                Soy profesional de la salud
              </Button>
            </Link>
          </div>
        </motion.div>
        <motion.div {...HERO_IMG_ANIM} className="relative hidden md:block">
          <div className="absolute -inset-4 bg-gradient-to-tr from-[hsl(var(--blush))] to-[hsl(var(--sky))] rounded-[2rem] rotate-2" />
          <img
            src={HERO_IMG}
            alt="RECETRANS"
            className="relative rounded-[2rem] object-contain w-full h-[480px] bg-white p-10"
          />
        </motion.div>
      </section>

      <section className="bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12">¿Cómo funciona?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border bg-background p-6 card-hover"
              >
                <span className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </span>
                <p className="font-heading font-semibold mb-2">
                  {i + 1}. {s.title}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-sm text-muted-foreground flex flex-wrap justify-between gap-4">
        <span>© {new Date().getFullYear()} TransSalud · Plataforma THAG</span>
        <span>Tuxpan 54, Roma Sur, Cuauhtémoc, Ciudad de México</span>
      </footer>
    </div>
  );
}
